import { LiveKitRoom } from "@livekit/components-react";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CallView from "./CallView";
import ConferenceBroadcastView from "./ConferenceBroadcastView";
import toast from "react-hot-toast";
import { useCallsService } from "../../Context/CallsContext";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import { useTranslate } from '@tolgee/react';
import { useAuthService } from "../../Context/AuthContext";
import WebSocketObserver from "../../Helpers/websocket/WebSocketObserver";
import { handleReceivedMessages } from "../../Helpers/chatHelper/ChatHelper";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useMeetingsService } from "../../Context/MeetingContext";
import MeetingJoinRequestQueue from "../Meetings/MeetingJoinRequestQueue";
import { useNotify, cancelMeetingNotices } from "../../Helpers/Notifications";
import { useNavigate } from "react-router";
import {
  closeIfSecondaryWindow,
  getWindowDetails,
} from "../../Helpers/navigationHelper";
import { useLinksquaredService } from "../../Context/LinksquaredContext";
import { getConferenceCallDetailsApiCall } from "../../API/conferences/conferencesService";
import {
  CONFERENCE_ROLE_ATTENDEE,
  DISABLED_TRANSLATION,
  REAL_TIME_TRANSLATION,
  SEQUENCIAL_TRANSLATION,
  VOICE_VOLUME_50,
  MEETING_LIFECYCLE_ENDED,
  MEETING_ENDED_REASON_QUOTA_EXHAUSTED,
} from "../../Constants/constantValues";
import { TranslatedMessagesSpeaker } from "../../Helpers/callHelper/TranslatedMessagesSpeaker";
import { normalizeConferenceLanguageKey } from "../../Helpers/callHelper/conferenceLanguageKey";
import LocalStorage from "../../Helpers/LocalStorage";
import languageSyncManager from "../../Helpers/callHelper/LanguageSyncHelper";
import { useRealtimeTranslation } from "../../hooks/useRealtimeTranslation";
import {
  shouldUseLegacyTranslatedSpeaker,
  shouldUseLiveKitAgentAudio,
} from "./callAudioMode";
import { createLogger } from "../../utils/logger";

// Conference coupling/translation signal. info/warn/error visible by default;
// debug gated behind localStorage.EMOTII_DEBUG="1"
const ccLog = createLogger("conf-coupling");

const CONFERENCE_SEQUENTIAL_SPEAKERS_GATE_TIMEOUT_MS = 2500;

const CallComponent = ({}) => {
  const { t } = useTranslate();
  const { setLoading } = useGenericComponentsStateService();
  const { setDeepLink } = useLinksquaredService();
  const {
    anonUser,
    handleLogoutUser,
    getCurrentUser,
    userRef,
    user,
  } = useAuthService();

  const {
    add_observer,
    remove_observer,
    sendCloseCallMessage,
    sendCancelSpeakMessage,
    sendRequestSpeakMessage,
    sendApproveJoinMeetingMessage,
    sendDenyJoinMeetingMessage,
    sendTranslationEnabledMessage,
    sendVoiceEnabledMessage,
    sendConferenceLanguageUpdate,
    updateConferenceChannelSubscription,
    clearConferenceChannelSubscription,
    sendRaiseHandMessage,
    sendLowerHandMessage,
    sendSyncRaisedHandsMessage,
    addConnectListener,
  } = useWebSocketDataService();

  const {
    getCallDetails,
    selectedCall,
    setSelectedCall,
    selectedCallAccess,
    setSelectedCallAccess,
    activeCallRef,
    answeredCallUUIDRef,
    answeredCallUUID,
    handleSetAsnweredCallUUID,
  } = useCallsService();

  const {
    setCurrentMeeting,
    currentMeeting,
    currentMeetingRef,
    getMeetingById,
  } = useMeetingsService();

  const navigate = useNavigate();
  const { notifyRecordingStarted, notifyTranscriptionStarted, notifyAiSummaryStarted } = useNotify();

  const [callChatConversation, setCallChatConversation] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [activePollBadge, setActivePollBadge] = useState(false);
  const callChatConversationIdRef = useRef(null);

  const [speakersQueue, setSpeakersQueue] = useState([]);
  const [raisedHands, setRaisedHands] = useState([]);
  const [speakMessagesList, setSpeakMessagesList] = useState([]);
  const [speechRecongnitionMessages, setSpeechRecognitionMessages] = useState(
    []
  );
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [translationType, setTranslationType] = useState(DISABLED_TRANSLATION);
  const [conferenceTranslationModeChangePending, setConferenceTranslationModeChangePending] = useState(false);

  const [joinRequestList, setJoinRequestList] = useState([]);
  const [meetingCapacity, setMeetingCapacity] = useState({
    maxParticipants: null,
    approvedParticipantsCount: 0,
  });
  const [callStartTime, setCallStartTime] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(false);
  const [transcriptExportEnabled, setTranscriptExportEnabled] = useState(false);

  const speakMessagesListRef = useRef([]);
  const meetingSettingsRef = useRef({ aiSummary: false, transcript: false });
  const joinNoticesShownForCallRef = useRef(null);
  const translationDisabledManuallyRef = useRef(false);
  const translationEnabledRef = useRef(false);
  const currentSpeechRecongnitionMessagesRef = useRef([]);
  const conferenceSubscriptionRef = useRef({
    callUuid: null,
    languageKey: null,
  });
  const pendingConferenceLanguageSwitchRef = useRef(null);
  const conferenceLanguageSwitchRetryTimeoutRef = useRef(null);
  const pendingConferenceTranslationUpdateRef = useRef(null);
  const conferenceTranslationUpdateRetryTimeoutRef = useRef(null);
  const conferenceSequentialSpeakersGateTimeoutRef = useRef(null);
  const latestConferenceSpeakersSnapshotRef = useRef({
    callUuid: null,
    receivedAt: 0,
  });

  const messagesToSpeakRef = useRef([]);
  const translatedMessagesSpeakerRef = useRef(new TranslatedMessagesSpeaker());
  const { speakAudio, selectedAudioOutput } = useCallsService();

  const [emotiiVoice, setEmotiiVoiceState] = useState(true);

  const useRealtimeApi = false;

  const conferenceLanguageKey = useMemo(() => {
    const fromUser = normalizeConferenceLanguageKey(user?.call_language);
    if (fromUser) return fromUser;
    return normalizeConferenceLanguageKey(LocalStorage.getConferenceCallLanguage());
  }, [user?.call_language]);

  const conferenceLanguageKeyRef = useRef(conferenceLanguageKey);

  useEffect(() => {
    conferenceLanguageKeyRef.current = conferenceLanguageKey;
    if (conferenceLanguageKey) {
      LocalStorage.setConferenceCallLanguage(conferenceLanguageKey);
    }
  }, [conferenceLanguageKey]);

  const clearConferenceLanguageSwitchRetry = useCallback(() => {
    if (conferenceLanguageSwitchRetryTimeoutRef.current) {
      clearTimeout(conferenceLanguageSwitchRetryTimeoutRef.current);
      conferenceLanguageSwitchRetryTimeoutRef.current = null;
    }
  }, []);

  const clearConferenceTranslationUpdateRetry = useCallback(() => {
    if (conferenceTranslationUpdateRetryTimeoutRef.current) {
      clearTimeout(conferenceTranslationUpdateRetryTimeoutRef.current);
      conferenceTranslationUpdateRetryTimeoutRef.current = null;
    }
  }, []);

  const clearConferenceSequentialSpeakersGateTimeout = useCallback(() => {
    if (conferenceSequentialSpeakersGateTimeoutRef.current) {
      clearTimeout(conferenceSequentialSpeakersGateTimeoutRef.current);
      conferenceSequentialSpeakersGateTimeoutRef.current = null;
    }
  }, []);

  const scheduleConferenceLanguageSwitchRetry = useCallback((trigger = "unknown") => {
    clearConferenceLanguageSwitchRetry();

    const pending = pendingConferenceLanguageSwitchRef.current;
    if (!pending) {
      return;
    }

    const attempts = pending.attempts || 0;
    if (attempts >= 5) {
      ccLog.error("[CallComponent] giving up on conference language switch ack", {
        trigger,
        callUuid: pending.callUuid,
        nextLanguageKey: pending.nextLanguageKey,
        attempts,
      });
      pendingConferenceLanguageSwitchRef.current = null;
      return;
    }

    const delayMs = attempts === 0 ? 1200 : 1500;
    conferenceLanguageSwitchRetryTimeoutRef.current = setTimeout(() => {
      const latestPending = pendingConferenceLanguageSwitchRef.current;
      const activeCall = activeCallRef.current?.details;

      if (!latestPending || !activeCall?.conference_id || activeCall?.uuid !== latestPending.callUuid) {
        clearConferenceLanguageSwitchRetry();
        return;
      }

      latestPending.attempts = (latestPending.attempts || 0) + 1;
      ccLog.warn("[CallComponent] retrying conference update_language (no ack yet)", {
        trigger,
        attempt: latestPending.attempts,
        callUuid: latestPending.callUuid,
        nextLanguageKey: latestPending.nextLanguageKey,
      });

      sendConferenceLanguageUpdate(latestPending.callUuid, latestPending.nextLanguageKey);
      scheduleConferenceLanguageSwitchRetry("ack-timeout");
    }, delayMs);
  }, [clearConferenceLanguageSwitchRetry, sendConferenceLanguageUpdate]);

  useEffect(() => {
    const callUuid = selectedCall?.uuid ?? activeCallRef.current?.details?.uuid;
    const isConference = Boolean(
      selectedCall?.conference_id ?? activeCallRef.current?.details?.conference_id
    );
    ccLog.debug("[EMOTII-DBG] conference subscription effect", {
      callUuid,
      conferenceId: selectedCall?.conference_id,
      activeCallRefConferenceId: activeCallRef.current?.details?.conference_id,
      isConference,
      conferenceLanguageKey,
      userCallLanguage: user?.call_language,
    });
    const pendingConferenceTranslation = pendingConferenceTranslationUpdateRef.current;

    if (
      pendingConferenceTranslation &&
      pendingConferenceTranslation.callUuid &&
      pendingConferenceTranslation.callUuid !== callUuid
    ) {
      clearPendingConferenceTranslationUpdate("call-changed");
    }

    if (!isConference || !callUuid) {
      const previous = conferenceSubscriptionRef.current;
      if (previous.callUuid && previous.languageKey) {
        clearConferenceChannelSubscription(
          previous.callUuid,
          previous.languageKey
        );
        conferenceSubscriptionRef.current = { callUuid: null, languageKey: null };
      }
      pendingConferenceLanguageSwitchRef.current = null;
      clearConferenceLanguageSwitchRetry();
      clearPendingConferenceTranslationUpdate("left-conference");
      return;
    }

    if (!conferenceLanguageKey) {
      const previous = conferenceSubscriptionRef.current;
      if (previous.callUuid && previous.languageKey) {
        clearConferenceChannelSubscription(
          previous.callUuid,
          previous.languageKey
        );
        conferenceSubscriptionRef.current = { callUuid: null, languageKey: null };
      }
      pendingConferenceLanguageSwitchRef.current = null;
      clearConferenceLanguageSwitchRetry();
      clearPendingConferenceTranslationUpdate("missing-language-key");
      return;
    }

    const previous = conferenceSubscriptionRef.current;
    if (
      previous.callUuid === callUuid &&
      previous.languageKey === conferenceLanguageKey
    ) {
      return;
    }

    if (
      previous.callUuid === callUuid &&
      previous.languageKey &&
      previous.languageKey !== conferenceLanguageKey
    ) {
      pendingConferenceLanguageSwitchRef.current = {
        callUuid,
        previousLanguageKey: previous.languageKey,
        nextLanguageKey: conferenceLanguageKey,
        attempts: 0,
      };
      clearConferenceLanguageSwitchRetry();
      ccLog.info("[CallComponent] conference language changed during active conference", {
        callUuid,
        previousLanguageKey: previous.languageKey,
        nextLanguageKey: conferenceLanguageKey,
        selectedCallUuid: selectedCall?.uuid,
        isConference,
      });
      sendConferenceLanguageUpdate(callUuid, conferenceLanguageKey);
      scheduleConferenceLanguageSwitchRetry("initial-send");
      return;
    }

    if (previous.callUuid && previous.languageKey) {
      clearConferenceChannelSubscription(previous.callUuid, previous.languageKey);
    }

    clearConferenceLanguageSwitchRetry();
    updateConferenceChannelSubscription(callUuid, conferenceLanguageKey);
    conferenceSubscriptionRef.current = {
      callUuid,
      languageKey: conferenceLanguageKey,
    };
  }, [
    clearConferenceChannelSubscription,
    clearConferenceLanguageSwitchRetry,
    conferenceLanguageKey,
    selectedCall?.conference_id,
    selectedCall?.uuid,
    sendConferenceLanguageUpdate,
    updateConferenceChannelSubscription,
  ]);

  useEffect(() => {
    return () => {
      clearConferenceLanguageSwitchRetry();
      pendingConferenceLanguageSwitchRef.current = null;
      const previous = conferenceSubscriptionRef.current;
      if (previous.callUuid && previous.languageKey) {
        clearConferenceChannelSubscription(previous.callUuid, previous.languageKey);
      }
    };
  }, [clearConferenceChannelSubscription, clearConferenceLanguageSwitchRetry]);

  const useLiveKitAgentAudio = useMemo(() => {
    return shouldUseLiveKitAgentAudio({ selectedCall });
  }, [selectedCall]);

  const useLegacyTranslatedSpeaker = useMemo(() => {
    return shouldUseLegacyTranslatedSpeaker({
      selectedCall,
      useRealtimeApi,
    });
  }, [selectedCall, useRealtimeApi]);

  const conferenceMediaPermissions = useMemo(() => {
    if (!selectedCall?.conference_id) {
      return null;
    }
    const prefs = LocalStorage.getConferenceMediaPermissions();
    if (!prefs || prefs.callUuid !== selectedCall?.uuid) {
      return null;
    }
    return prefs;
  }, [selectedCall?.conference_id, selectedCall?.uuid]);

  const conferenceRole =
    conferenceMediaPermissions?.role ?? selectedCall?.conference_role;

  const [broadcastChatAllowed, setBroadcastChatAllowed] = useState(false);
  const broadcastPollsAllowed = selectedCallAccess?.type === "mux_stream"
    ? (conferenceMediaPermissions ? !!conferenceMediaPermissions.pollsAllowed : !!selectedCall?.conference_id)
    : false;

  useEffect(() => {
    setBroadcastChatAllowed(
      selectedCallAccess?.type === "mux_stream"
        ? (conferenceMediaPermissions ? !!conferenceMediaPermissions.chatAllowed : true)
        : false
    );
  }, [selectedCallAccess?.type, conferenceMediaPermissions]);

  useEffect(() => {
    if (selectedCallAccess?.type !== "mux_stream" || !selectedCall?.id) return;

    const observer = {
      channelName: "ConversationsChannel",
      update: (rawData) => {
        const wsData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        const matchesCall =
          (wsData.call_id && wsData.call_id === selectedCall.id) ||
          (wsData.conference_call_id && wsData.conference_call_id === selectedCall.id);
        if (matchesCall && wsData.id) {
          callChatConversationIdRef.current = wsData.id;
          setCallChatConversation({ ...wsData });
          setUnreadChatCount(wsData.unread_messages_ids?.length || 0);
          return;
        }
        if (wsData.id === callChatConversationIdRef.current) {
          setCallChatConversation({ ...wsData });
          setUnreadChatCount(wsData.unread_messages_ids?.length || 0);
        }
      },
    };

    add_observer(observer);
    return () => remove_observer(observer);
  }, [selectedCallAccess?.type, selectedCall?.id]);

  useEffect(() => {
    if (selectedCallAccess?.type !== "mux_stream" || !selectedCall?.conference_id) return;

    const observer = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const msg = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
          if (msg.message_type === "poll_launched") setActivePollBadge(true);
          if (msg.message_type === "poll_ended") setActivePollBadge(false);
        } catch {}
      },
    };

    add_observer(observer);
    return () => remove_observer(observer);
  }, [selectedCallAccess?.type, selectedCall?.conference_id]);

  useEffect(() => {
    if (selectedCallAccess?.type !== "mux_stream" || !selectedCall?.conference_id) return;

    const observer = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const msg = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
          if (msg.message_type === "chat_toggled") {
            setBroadcastChatAllowed(!!msg.allow_chat);
          }
        } catch {}
      },
    };

    add_observer(observer);
    return () => remove_observer(observer);
  }, [selectedCallAccess?.type, selectedCall?.conference_id]);

  useEffect(() => {
    if (selectedCallAccess?.type !== "mux_stream") return;
    if (selectedCallAccess?.raw_playback_url) return;

    const callUuid =
      activeCallRef.current?.details?.uuid ?? selectedCall?.uuid;
    if (!callUuid || !conferenceLanguageKey) return;

    const MAX_ATTEMPTS = 5;
    const RETRY_MS = 3000;
    let attempts = 0;
    let timer = null;

    const requestStreams = () => {
      attempts += 1;
      ccLog.info("[CallComponent] re-requesting attendee broadcast streams", {
        callUuid,
        conferenceLanguageKey,
        attempt: attempts,
      });
      sendConferenceLanguageUpdate(callUuid, conferenceLanguageKey);
      if (attempts < MAX_ATTEMPTS) {
        timer = setTimeout(requestStreams, RETRY_MS);
      }
    };

    requestStreams();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    selectedCallAccess?.type,
    selectedCallAccess?.raw_playback_url,
    selectedCall?.uuid,
    conferenceLanguageKey,
    sendConferenceLanguageUpdate,
  ]);

  const shouldPublishAudio = useMemo(() => {
    if (!selectedCall?.conference_id) {
      return true;
    }
    return conferenceRole !== CONFERENCE_ROLE_ATTENDEE;
  }, [conferenceRole, selectedCall?.conference_id]);

  const shouldPublishVideo = useMemo(() => {
    if (!selectedCall?.conference_id) {
      return selectedCall?.video;
    }
    return conferenceRole !== CONFERENCE_ROLE_ATTENDEE && selectedCall?.video;
  }, [conferenceRole, selectedCall?.conference_id, selectedCall?.video]);

  const {
    isConnected: realtimeConnected,
    translations: realtimeTranslations,
    setTtsEnabled: setRealtimeTtsEnabled,
    stopAudioPlayback: stopRealtimeAudioPlayback,
    isAudioPlaying: isRealtimeAudioPlaying,
  } = useRealtimeTranslation({
    enabled: useRealtimeApi,
    language: user?.call_language,
    ttsEnabled: emotiiVoice,
    outputDeviceId: selectedAudioOutput,
  });

  const [currentSpeakerIsActive, setCurrentSpeakerIsActive] = useState(false);
  const emotiiVoiceRef = useRef(true);
  const lastVoiceStateRef = useRef({ callUuid: null, voice: null });

  const setEmotiiVoice = useCallback((nextValue, source = "unknown") => {
    setEmotiiVoiceState((previousValue) => {
      const resolvedValue =
        typeof nextValue === "function" ? nextValue(previousValue) : nextValue;

      if (resolvedValue === previousValue) {
        return previousValue;
      }

      ccLog.debug("[CallComponent][VoiceState] setEmotiiVoice", {
        source,
        previousValue,
        nextValue: resolvedValue,
        callUuid: activeCallRef.current?.details?.uuid,
        conferenceId: activeCallRef.current?.details?.conference_id,
        userVoiceEnabled: user?.voice_enabled,
        localStorageVoice: LocalStorage.getEmotiiVoice(),
      });

      return resolvedValue;
    });
  }, [user?.voice_enabled]);

  const CallChannelObserver = new WebSocketObserver(
    "CallChannel",
    (message) => {
      handleCallWsResponse(message);
    }
  );

  const ConferenceCallChannelObserver = new WebSocketObserver(
    "ConferenceCallChannel",
    (message) => {
      handleCallWsResponse(message);
    }
  );

  const MeetingChannelObserver = new WebSocketObserver(
    "MeetingChannel",
    (message) => {
      handleMeetingWsResponse(message);
    }
  );

  const handleMeetingWsResponse = (message) => {
    let response = message;

    if (!currentMeetingRef.current) {
      return;
    }
    if (typeof response === "string") {
      response = JSON.parse(message);
    }

    if (response.meeting_id === currentMeetingRef.current.id) {
      if (response.requests) {
        setJoinRequestList(response.requests);
      }
      if (
        response.max_participants !== undefined ||
        response.approved_participants_count !== undefined
      ) {
        setMeetingCapacity({
          maxParticipants: response.max_participants ?? null,
          approvedParticipantsCount: response.approved_participants_count ?? 0,
        });
      }
      if (response.status === "capacity_reached") {
        toast.error(t("meetings.meeting_full_approve_failed"));
      }
      if (response.status === MEETING_LIFECYCLE_ENDED) {
        toast(t(response.reason === MEETING_ENDED_REASON_QUOTA_EXHAUSTED ? "meetings.meeting_ended_quota" : "meetings.meeting_ended_scheduled"));
      }
    }
  };

  const handleCallWsResponse = (response) => {
    ccLog.debug("[EMOTII-DBG] CallChannel WS message received:", JSON.stringify(response));
    if (
      response.enabled &&
      (response.translation_type === REAL_TIME_TRANSLATION ||
        response.translation_type === SEQUENCIAL_TRANSLATION)
    ) {
      if (activeCallRef.current.details.uuid === response.call_uuid) {
        ccLog.info("[EMOTII-DBG] Enabling translation:", response.translation_type);
        handleEnableTranslation(response.translation_type);
        resolvePendingConferenceTranslationUpdate(
          response.call_uuid,
          response.translation_type,
          "ws-enabled"
        );
      }
    }

    if (response.enabled === false && response.translation_type === null) {
      if (activeCallRef.current.details.uuid === response.call_uuid) {
        ccLog.info("[EMOTII-DBG] Disabling translation");
        handleDisableTranslation();
        resolvePendingConferenceTranslationUpdate(
          response.call_uuid,
          DISABLED_TRANSLATION,
          "ws-disabled"
        );
      }
    }

    if (response.message_type === "language_updated") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        ccLog.info("[CallComponent][DEBUG] language_updated received", {
          language: response.language,
          oldLanguage: response.old_language,
          rawPlaybackUrl: response.raw_playback_url,
          ttsPlaybackUrl: response.tts_playback_url,
          provisioning: response.provisioning,
          selectedCallAccessType: selectedCallAccess?.type,
        });
        setSelectedCallAccess((prev) => {
          if (!prev || prev.type !== "mux_stream") {
            ccLog.warn("[CallComponent][DEBUG] language_updated DROPPED - selectedCallAccess not mux_stream", {
              language: response.language,
              prevType: prev?.type,
            });
            return prev;
          }

          const raw_playback_url = response.raw_playback_url ?? prev.raw_playback_url;
          const raw_playback_id = response.raw_playback_id ?? prev.raw_playback_id;

          let streams = prev.streams ?? [];
          if (response.language) {
            const hasStream = streams.some((s) => s.language_key === response.language);
            streams = hasStream
              ? streams.map((s) =>
                  s.language_key === response.language
                    ? {
                        ...s,
                        playback_url: response.tts_playback_url ?? s.playback_url,
                        playback_id: response.tts_playback_id ?? s.playback_id,
                      }
                    : s
                )
              : [
                  ...streams,
                  {
                    language_key: response.language,
                    playback_url: response.tts_playback_url ?? null,
                    playback_id: response.tts_playback_id ?? null,
                  },
                ];
          }

          const provisioning = response.provisioning ?? false;

          return { ...prev, raw_playback_url, raw_playback_id, streams, provisioning };
        });
        const confirmedSwitch = pendingConferenceLanguageSwitchRef.current;
        if (confirmedSwitch?.nextLanguageKey) {
          conferenceSubscriptionRef.current = {
            callUuid: confirmedSwitch.callUuid,
            languageKey: confirmedSwitch.nextLanguageKey,
          };
        }
        pendingConferenceLanguageSwitchRef.current = null;
        clearConferenceLanguageSwitchRetry();
      }
    }

    if (response.update === "reload_call_details") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        handleGetActiveCallNewDetails(response.call_uuid);
      }
    }

    if (response.update === "close") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        resetCall();
      }
    }

    if (response.update === "force_disconnect") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        toast.error(t('call_modal.you_were_removed'));
        resetCall();
      }
    }

    if (response.update === "participant_kicked") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        if (response.kicked_user_id === userRef.current?.id) {
          toast.error(t('call_modal.you_were_kicked'));
          resetCall();
        } else {
          handleGetActiveCallNewDetails(response.call_uuid);
        }
      }
    }

    if (response.update === "participant_muted") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        if (response.muted_user_id === userRef.current?.id) {
          toast(t('call_modal.you_were_muted'));
        }
        handleGetActiveCallNewDetails(response.call_uuid);
      }
    }

    if (response.update === "participant_unmuted") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        if (response.muted_user_id === userRef.current?.id) {
          toast(t('call_modal.you_were_unmuted'));
        }
        handleGetActiveCallNewDetails(response.call_uuid);
      }
    }

    if (response.update === "conference_call_speakers" || response.update === "call_speakers") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        handleIcomingCallSpeakersList(response);
      }
    }

    if (response.update === "raised_hands") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        setRaisedHands(response.raised_hands || []);
      }
    }

    if (response.type === "recording_started" || response.type === "recording_stopped") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        setIsRecording(response.recording_in_progress);
        if (response.type === "recording_started" && response.triggered_by) {
          notifyRecordingStarted(response.triggered_by);
        }
      }
    }

    if (response.type === "meeting_settings_updated") {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        if (response.settings) {
          const previous = meetingSettingsRef.current;
          const next = {
            aiSummary: response.settings.ai_summary_enabled,
            transcript: response.settings.transcript_export_enabled,
          };
          meetingSettingsRef.current = next;
          if (next.transcript && !previous.transcript) {
            notifyTranscriptionStarted(response.updated_by);
          }
          if (next.aiSummary && !previous.aiSummary) {
            notifyAiSummaryStarted(response.updated_by);
          }
          setAiSummaryEnabled(response.settings.ai_summary_enabled);
          setTranscriptExportEnabled(response.settings.transcript_export_enabled);
        }
      }
    }

    if (response.original_message) {
      if (activeCallRef.current?.details?.uuid === response.call_uuid) {
        handleIconmingTranslationMessages(response);
      }
    }

  };

  const handleEnableTranslation = (nextTranslationType) => {
    translationEnabledRef.current = true;
    setTranslationEnabled(true);
    setTranslationType(nextTranslationType);
  };

  const handleDisableTranslation = () => {
    translationEnabledRef.current = false;
    setTranslationEnabled(false);
    setTranslationType(DISABLED_TRANSLATION);
  };

  const normalizeRequestedTranslationType = (nextTranslationType) => {
    if (
      nextTranslationType === REAL_TIME_TRANSLATION ||
      nextTranslationType === SEQUENCIAL_TRANSLATION
    ) {
      return nextTranslationType;
    }

    return DISABLED_TRANSLATION;
  };

  const clearPendingConferenceTranslationUpdate = (reason = "unknown") => {
    const pending = pendingConferenceTranslationUpdateRef.current;
    if (pending) {
      ccLog.debug("[CallComponent] clearing pending conference translation update", {
        reason,
        callUuid: pending.callUuid,
        requestedTranslationType: pending.requestedTranslationType,
        retryCount: pending.retryCount,
      });
    }

    pendingConferenceTranslationUpdateRef.current = null;
    setConferenceTranslationModeChangePending(false);
    clearConferenceTranslationUpdateRetry();
    clearConferenceSequentialSpeakersGateTimeout();
  };

  const tryResolveConferenceSequentialSpeakersGate = (callUuid, source = "unknown") => {
    const pending = pendingConferenceTranslationUpdateRef.current;
    if (!pending || pending.callUuid !== callUuid) {
      return false;
    }

    if (pending.requestedTranslationType !== SEQUENCIAL_TRANSLATION) {
      return false;
    }

    const latestSnapshot = latestConferenceSpeakersSnapshotRef.current;
    if (
      latestSnapshot.callUuid !== callUuid ||
      latestSnapshot.receivedAt < pending.createdAt
    ) {
      return false;
    }

    clearPendingConferenceTranslationUpdate(`confirmed:${source}:sequential-speakers`);
    return true;
  };

  const scheduleConferenceTranslationUpdateRetry = (trigger = "unknown") => {
    clearConferenceTranslationUpdateRetry();

    const pending = pendingConferenceTranslationUpdateRef.current;
    if (!pending) {
      return;
    }

    const delayMs = pending.retryCount === 0 ? 900 : 1400;

    conferenceTranslationUpdateRetryTimeoutRef.current = setTimeout(() => {
      const latestPending = pendingConferenceTranslationUpdateRef.current;
      const activeCall = activeCallRef.current?.details;

      if (
        !latestPending ||
        !activeCall?.conference_id ||
        activeCall?.uuid !== latestPending.callUuid
      ) {
        clearConferenceTranslationUpdateRetry();
        return;
      }

      if (latestPending.retryCount < 1) {
        latestPending.retryCount += 1;
        ccLog.warn(
          "[CallComponent] retrying conference translation mode update (no echo yet)",
          {
            trigger,
            callUuid: latestPending.callUuid,
            requestedTranslationType: latestPending.requestedTranslationType,
            retryCount: latestPending.retryCount,
          }
        );

        sendTranslationEnabledMessage(
          latestPending.callUuid,
          latestPending.requestedTranslationType !== DISABLED_TRANSLATION,
          latestPending.requestedTranslationType === DISABLED_TRANSLATION
            ? null
            : latestPending.requestedTranslationType,
          "ConferenceCallChannel"
        );
        scheduleConferenceTranslationUpdateRetry("retry-send");
        return;
      }

      ccLog.warn(
        "[CallComponent] conference translation mode echo timeout, reconciling via call details",
        {
          trigger,
          callUuid: latestPending.callUuid,
          requestedTranslationType: latestPending.requestedTranslationType,
        }
      );

      clearPendingConferenceTranslationUpdate("echo-timeout");
      handleGetActiveCallNewDetails(latestPending.callUuid);
    }, delayMs);
  };

  const markPendingConferenceTranslationUpdate = (callUuid, nextTranslationType) => {
    if (!callUuid) {
      return;
    }

    pendingConferenceTranslationUpdateRef.current = {
      callUuid,
      requestedTranslationType: normalizeRequestedTranslationType(nextTranslationType),
      retryCount: 0,
      createdAt: Date.now(),
      waitingForSpeakerSnapshot: false,
    };
    setConferenceTranslationModeChangePending(true);

    scheduleConferenceTranslationUpdateRetry("initial-send");
  };

  const resolvePendingConferenceTranslationUpdate = (
    callUuid,
    nextTranslationType,
    source = "unknown"
  ) => {
    const pending = pendingConferenceTranslationUpdateRef.current;
    if (!pending || !callUuid || pending.callUuid !== callUuid) {
      return;
    }

    const normalizedNextType = normalizeRequestedTranslationType(nextTranslationType);
    if (pending.requestedTranslationType !== normalizedNextType) {
      return;
    }

    if (normalizedNextType === SEQUENCIAL_TRANSLATION) {
      clearConferenceTranslationUpdateRetry();

      if (tryResolveConferenceSequentialSpeakersGate(callUuid, `${source}:cached`)) {
        return;
      }

      pending.waitingForSpeakerSnapshot = true;
      setConferenceTranslationModeChangePending(true);
      clearConferenceSequentialSpeakersGateTimeout();
      conferenceSequentialSpeakersGateTimeoutRef.current = setTimeout(() => {
        const latestPending = pendingConferenceTranslationUpdateRef.current;
        if (
          !latestPending ||
          latestPending.callUuid !== callUuid ||
          latestPending.requestedTranslationType !== SEQUENCIAL_TRANSLATION ||
          !latestPending.waitingForSpeakerSnapshot
        ) {
          return;
        }

        ccLog.warn(
          "[CallComponent] sequential translation gate timeout while waiting for speaker snapshot",
          {
            callUuid,
            source,
          }
        );
        clearPendingConferenceTranslationUpdate("sequential-speakers-timeout");
      }, CONFERENCE_SEQUENTIAL_SPEAKERS_GATE_TIMEOUT_MS);
      return;
    }

    clearPendingConferenceTranslationUpdate(`confirmed:${source}`);
  };

  const handleSendTranslationEnabledMessage = (requestedTranslationType, options = {}) => {
    const callDetails = activeCallRef.current?.details;
    const callUuid = callDetails?.uuid;
    if (!callUuid) {
      return;
    }

    ccLog.debug('[Translation] handleSendTranslationEnabledMessage called with:', requestedTranslationType);
    ccLog.debug('[Translation] Call UUID:', callUuid);
    ccLog.debug('[Translation] Call details:', callDetails);

    const channelName = callDetails?.conference_id
      ? "ConferenceCallChannel"
      : "CallChannel";

    if (callDetails?.conference_id && options?.userInitiated === true) {
      const normalizedRequestedType =
        normalizeRequestedTranslationType(requestedTranslationType);
      const currentConferenceTranslationType = normalizeRequestedTranslationType(
        translationEnabledRef.current ? translationType : DISABLED_TRANSLATION
      );
      const pendingConferenceTranslationUpdate =
        pendingConferenceTranslationUpdateRef.current;

      if (pendingConferenceTranslationUpdate?.callUuid === callUuid) {
        ccLog.debug(
          "[CallComponent] ignoring conference translation mode update while pending",
          {
            callUuid,
            requestedTranslationType: normalizedRequestedType,
            pendingRequestedTranslationType:
              pendingConferenceTranslationUpdate.requestedTranslationType,
          }
        );
        return;
      }

      if (normalizedRequestedType === currentConferenceTranslationType) {
        ccLog.debug(
          "[CallComponent] ignoring duplicate conference translation mode update",
          {
            callUuid,
            requestedTranslationType: normalizedRequestedType,
            currentConferenceTranslationType,
            callDetailsTranslationType: callDetails?.translation_type,
          }
        );
        return;
      }

      if (normalizedRequestedType === DISABLED_TRANSLATION) {
        handleDisableTranslation();
      } else {
        handleEnableTranslation(normalizedRequestedType);
      }

      markPendingConferenceTranslationUpdate(callUuid, normalizedRequestedType);
    }

    const sendModeUpdate = () => {
      if (requestedTranslationType === DISABLED_TRANSLATION) {
        ccLog.info('[Translation] Sending DISABLED message');
        sendTranslationEnabledMessage(
          callUuid,
          false,
          null,
          channelName
        );
        return;
      }

      ccLog.info('[Translation] Sending ENABLED message with type:', requestedTranslationType);
      sendTranslationEnabledMessage(
        callUuid,
        true,
        requestedTranslationType,
        channelName
      );
    };

    if (channelName === "ConferenceCallChannel" && conferenceLanguageKey) {
      updateConferenceChannelSubscription(callUuid, conferenceLanguageKey);
    }

    sendModeUpdate();
  };
  const handleSendTranslationEnabledMessageRef = useRef(handleSendTranslationEnabledMessage);
  handleSendTranslationEnabledMessageRef.current = handleSendTranslationEnabledMessage;
  const latestTranslationTypeRef = useRef(translationType);
  latestTranslationTypeRef.current = translationType;

  const handleIcomingCallSpeakersList = (speakerList) => {
    let queue = speakerList.speakers;
    setSpeakersQueue(queue);

    if (speakerList?.call_uuid) {
      latestConferenceSpeakersSnapshotRef.current = {
        callUuid: speakerList.call_uuid,
        receivedAt: Date.now(),
      };

      const pending = pendingConferenceTranslationUpdateRef.current;
      if (
        pending &&
        pending.callUuid === speakerList.call_uuid &&
        pending.requestedTranslationType === SEQUENCIAL_TRANSLATION &&
        pending.waitingForSpeakerSnapshot
      ) {
        tryResolveConferenceSequentialSpeakersGate(
          speakerList.call_uuid,
          "speaker-update"
        );
      }
    }
  };

  const removeDuplicateMessageForMessage = (list, message) => {
    let filtered = list.filter(
      (item) =>
        !(
          item.original_message === message.original_message &&
          item.user_id === message.user_id
        )
    );
    return filtered;
  };

  const handleIconmingTranslationMessages = (message) => {
    const isConference = Boolean(activeCallRef.current?.details?.conference_id);
    const existingMessage = isConference
      ? currentSpeechRecongnitionMessagesRef.current.find(
          (item) => item?.id === message?.id
        )
      : null;
    const selectedLanguageKey = isConference
      ? conferenceLanguageKeyRef.current ||
        message?.selected_language_key ||
        existingMessage?.selected_language_key
      : null;
    const messageWithLanguageKey =
      isConference && selectedLanguageKey
        ? { ...message, selected_language_key: selectedLanguageKey }
        : message;

    currentSpeechRecongnitionMessagesRef.current =
      removeDuplicateMessageForMessage(
        currentSpeechRecongnitionMessagesRef.current,
        messageWithLanguageKey
      );

    const newMessages = handleReceivedMessages(
      currentSpeechRecongnitionMessagesRef.current,
      messageWithLanguageKey
    );

    const newMessagesSorted = newMessages.sort((a, b) =>
      a.device_sent_date < b.device_sent_date ? 1 : -1
    );

    currentSpeechRecongnitionMessagesRef.current = newMessagesSorted;

    setSpeechRecognitionMessages(currentSpeechRecongnitionMessagesRef.current);

  };

  const handleSendCancelSpeakMessage = () => {
    const callUuid = activeCallRef.current?.details?.uuid;
    if (!callUuid) {
      return;
    }
    const channelName = activeCallRef.current?.details?.conference_id
      ? "ConferenceCallChannel"
      : "CallChannel";
    sendCancelSpeakMessage(callUuid, channelName);
  };

  const handleRequestSpeakMessage = (payload) => {
    const callUuid = activeCallRef.current?.details?.uuid;
    if (!callUuid) {
      return;
    }
    const channelName = activeCallRef.current?.details?.conference_id
      ? "ConferenceCallChannel"
      : "CallChannel";
    if (payload) {
      sendRequestSpeakMessage(callUuid, payload, channelName);
      return;
    }
    sendRequestSpeakMessage(callUuid, {}, channelName);
  };

  const handleRaiseHand = () => {
    const details = activeCallRef.current?.details;
    if (!details?.uuid) {
      return;
    }
    sendRaiseHandMessage(details.uuid, details.conference_id ? "ConferenceCallChannel" : "CallChannel");
  };

  const handleLowerHand = () => {
    const details = activeCallRef.current?.details;
    if (!details?.uuid) {
      return;
    }
    sendLowerHandMessage(details.uuid, details.conference_id ? "ConferenceCallChannel" : "CallChannel");
  };

  useEffect(() => {
    setRaisedHands(selectedCall?.raised_hands || []);

    if (!selectedCall?.uuid || selectedCallAccess?.type === "mux_stream") {
      return undefined;
    }

    const callUuid = selectedCall.uuid;
    const channelName = selectedCall.conference_id ? "ConferenceCallChannel" : "CallChannel";
    const sync = () => sendSyncRaisedHandsMessage(callUuid, channelName);

    sync();
    return addConnectListener(sync);
  }, [selectedCall?.uuid, selectedCall?.conference_id, selectedCallAccess?.type]);

  const resetCall = async () => {
    ccLog.debug("[EMOTII-DBG] resetCall() called — stack:", new Error().stack?.split('\n').slice(1, 5).join(' | '));
    toast.dismiss(); //close all notifications
    const isConferenceScopedSession =
      sessionStorage.getItem("conference_scoped_auth") === "1";
    const shouldLogoutConferenceGuest =
      activeCallRef.current?.details?.conference_id &&
      (user?.conference_guest || isConferenceScopedSession);
    const activeCallUuid = activeCallRef.current?.details?.uuid;
    if (activeCallUuid) {
      sendCloseCallMessage(activeCallUuid);
    }
    clearPendingConferenceTranslationUpdate("reset-call");
    handleSetAsnweredCallUUID(null);
    setSelectedCall(null);
    setSelectedCallAccess(null);
    activeCallRef.current = null;
    currentSpeechRecongnitionMessagesRef.current = [];
    setSpeechRecognitionMessages([]);
    setSpeakMessagesList([]);
    setSpeakersQueue([]);
    speakMessagesListRef.current = [];
    setTranslationEnabled(false);
    setTranslationType(DISABLED_TRANSLATION);
    translationDisabledManuallyRef.current = false;
    translationEnabledRef.current = false;
    setCallStartTime(null);
    setIsRecording(false);
    setAiSummaryEnabled(false);
    setTranscriptExportEnabled(false);
    cancelMeetingNotices();

    if (currentMeetingRef.current) {
      setDeepLink("");
      if (anonUser.current) {
        handleLogoutUser();
      }
    }

    if (shouldLogoutConferenceGuest) {
      handleLogoutUser();
      signOut(auth).catch(() => {});
      sessionStorage.removeItem("conference_scoped_auth");
    }

    currentMeetingRef.current = null;
    setCurrentMeeting(null);

    const { isSecondaryWindow } = (await getWindowDetails()) || {};
    if (isSecondaryWindow) {
      closeIfSecondaryWindow();
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleGetActiveCallNewDetails = (uuid) => {
    const conferenceId = activeCallRef.current?.details?.conference_id;

    if (conferenceId) {
      getConferenceCallDetailsApiCall(
        conferenceId,
        (response) => {
          const callDetails =
            response?.data?.details ||
            response?.data?.call ||
            response?.data?.conference_call;
          if (!callDetails) {
            return;
          }
          const withConferenceFlag = { ...callDetails, conference_id: conferenceId };
          const newAccess = response?.data?.access;
          const prevAccess = activeCallRef.current?.access;
          const keepLiveToken = Boolean(prevAccess?.token);
          const nextAccess = keepLiveToken ? prevAccess : newAccess;
          activeCallRef.current = {
            details: withConferenceFlag,
            access: nextAccess,
          };
          setSelectedCall({ ...withConferenceFlag });
          if (!keepLiveToken) {
            setSelectedCallAccess(newAccess);
          }
        },
        (error) => {
          ccLog.error(error);
        }
      );
      return;
    }

    getCallDetails(
      uuid,
      (response) => {
        activeCallRef.current = response.data;
        // setSelectedCall(response.data.details);
        setSelectedCall({...response.data.details});
        setIsRecording(response.data.details?.recording_in_progress ?? false);
      },
      (error) => {
        ccLog.error(error);
      }
    );
  };

  const handleGetMeetingById = (meetingId) => {
    getMeetingById(
      meetingId,
      (response) => {
        const meeting = response.data.meeting;
        let pendingParticipants = meeting.participants.filter(
          (item) => item.status === "pending"
        );
        setJoinRequestList(pendingParticipants);
        setMeetingCapacity({
          maxParticipants: meeting.max_participants ?? null,
          approvedParticipantsCount: meeting.approved_participants_count ?? 0,
        });
      },
      (error) => {}
    );
  };

  useEffect(() => {
    if (!userRef.current && !anonUser.current) {
      getCurrentUser(
        (response) => {},
        () => {}
      );
    }
  }, []);

  useEffect(() => {
    add_observer(CallChannelObserver);
    add_observer(ConferenceCallChannelObserver);
    add_observer(MeetingChannelObserver);
    return () => {
      remove_observer(CallChannelObserver);
      remove_observer(ConferenceCallChannelObserver);
      remove_observer(MeetingChannelObserver);

      // Reset the call on unload
      handleSetAsnweredCallUUID(null);
    };
  }, []);

  useEffect(() => {
    if (currentMeetingRef.current) {
      handleGetMeetingById(currentMeetingRef.current.id);
    }
  }, [currentMeeting]);

  useEffect(() => {
    if (selectedCall?.id) {
      setIsRecording(selectedCall.recording_in_progress ?? false);
    }
  }, [selectedCall?.id]);

  useEffect(() => {
    emotiiVoiceRef.current = emotiiVoice;

    if (!useRealtimeApi) {
      return;
    }

    setRealtimeTtsEnabled(emotiiVoice);

    if (!emotiiVoice) {
      stopRealtimeAudioPlayback();
    }
  }, [
    emotiiVoice,
    useRealtimeApi,
    setRealtimeTtsEnabled,
    stopRealtimeAudioPlayback,
  ]);

  useEffect(() => {
    translatedMessagesSpeakerRef.current.stop();
    const stored = LocalStorage.getEmotiiVoiceVolume(selectedCall?.uuid);
    const initialVolume = stored !== null ? stored : VOICE_VOLUME_50;
    translatedMessagesSpeakerRef.current.setVolume(initialVolume / 100);
  }, []);

  useEffect(() => {
    if (!currentSpeakerIsActive) {
      return;
    }
    translatedMessagesSpeakerRef.current.stop();
    if (useRealtimeApi) {
      stopRealtimeAudioPlayback();
    }
  }, [currentSpeakerIsActive, stopRealtimeAudioPlayback, useRealtimeApi]);

  // stop() marks messages as spoken, so only call it on mode transitions
  useEffect(() => {
    if (!useLegacyTranslatedSpeaker) {
      translatedMessagesSpeakerRef.current.stop();
    }
  }, [useLegacyTranslatedSpeaker]);

  useEffect(() => {
    if (!useLegacyTranslatedSpeaker) {
      return;
    }

    if (!selectedCall?.conference_id) {
      translatedMessagesSpeakerRef.current.stop();
      translatedMessagesSpeakerRef.current.markAllAsSpoken(
        currentSpeechRecongnitionMessagesRef.current
      );
      return;
    }

    if (!emotiiVoice) {
      translatedMessagesSpeakerRef.current.stop();
      translatedMessagesSpeakerRef.current.markAllAsSpoken(
        currentSpeechRecongnitionMessagesRef.current
      );
      return;
    }

    if (currentSpeakerIsActive) {
      return;
    }

    translatedMessagesSpeakerRef.current.processMessages(
      currentSpeechRecongnitionMessagesRef.current,
      {
        speakAudio,
        user: userRef.current,
        outputDeviceId: selectedAudioOutput,
      }
    );
  }, [
    speechRecongnitionMessages,
    currentSpeakerIsActive,
    emotiiVoice,
    useLegacyTranslatedSpeaker,
    useRealtimeApi,
    speakAudio,
    selectedAudioOutput,
    selectedCall?.conference_id,
  ]);

  useEffect(() => {
    ccLog.debug("[EMOTII-DBG] selectedCallAccess changed — endpoint:", selectedCallAccess?.endpoint, "| hasToken:", !!selectedCallAccess?.token);
  }, [selectedCallAccess]);

  useEffect(() => {
    if (!selectedCall) {
      return;
    }

    if (selectedCall.conference_id) {
      const pendingConferenceTranslation = pendingConferenceTranslationUpdateRef.current;
      const selectedCallTranslationType =
        selectedCall.translation_type ?? DISABLED_TRANSLATION;

      if (
        pendingConferenceTranslation &&
        pendingConferenceTranslation.callUuid === selectedCall.uuid &&
        pendingConferenceTranslation.requestedTranslationType !==
          normalizeRequestedTranslationType(selectedCallTranslationType)
      ) {
        ccLog.debug(
          "[CallComponent] skipping stale conference translation_type from selectedCall while waiting for echo",
          {
            callUuid: selectedCall.uuid,
            selectedCallTranslationType,
            pendingRequestedTranslationType:
              pendingConferenceTranslation.requestedTranslationType,
          }
        );
        return;
      }

      if (selectedCall.translation_type === REAL_TIME_TRANSLATION) {
        handleEnableTranslation(REAL_TIME_TRANSLATION);
      }
      if (selectedCall.translation_type === SEQUENCIAL_TRANSLATION) {
        handleEnableTranslation(SEQUENCIAL_TRANSLATION);
      }
      if (selectedCall.translation_type === null) {
        handleDisableTranslation();
      }

      resolvePendingConferenceTranslationUpdate(
        selectedCall.uuid,
        selectedCallTranslationType,
        "selectedCall"
      );
      return;
    }

    if (selectedCall.translation_type === REAL_TIME_TRANSLATION) {
      handleEnableTranslation(REAL_TIME_TRANSLATION);
    }
    if (selectedCall.translation_type === SEQUENCIAL_TRANSLATION) {
      handleEnableTranslation(SEQUENCIAL_TRANSLATION);
    }
    if (selectedCall.translation_type === null) {
      handleDisableTranslation();
    }
  }, [selectedCall]);

  // Initialize meeting settings from selectedCall
  useEffect(() => {
    if (selectedCall) {
      setAiSummaryEnabled(selectedCall.ai_summary_enabled || false);
      setTranscriptExportEnabled(selectedCall.transcript_export_enabled || false);
      meetingSettingsRef.current = {
        aiSummary: selectedCall.ai_summary_enabled || false,
        transcript: selectedCall.transcript_export_enabled || false,
      };
    }
  }, [selectedCall?.ai_summary_enabled, selectedCall?.transcript_export_enabled]);

  useEffect(() => {
    if (!selectedCall?.meeting_id || joinNoticesShownForCallRef.current === selectedCall.id) return;
    joinNoticesShownForCallRef.current = selectedCall.id;

    if (selectedCall.recording_in_progress) {
      notifyRecordingStarted(selectedCall.recording_started_by);
    }
    if (selectedCall.transcript_export_enabled) {
      notifyTranscriptionStarted(selectedCall.transcript_export_started_by);
    }
    if (selectedCall.ai_summary_enabled) {
      notifyAiSummaryStarted(selectedCall.ai_summary_started_by);
    }
  }, [selectedCall?.id]);

  useEffect(() => {
    const storedVolume = LocalStorage.getEmotiiVoiceVolume(selectedCall?.uuid);
    const initialVolume = storedVolume !== null ? storedVolume : VOICE_VOLUME_50;
    const voiceEnabled = initialVolume > 0;
    ccLog.debug("[CallComponent] voice_enabled derived from stored volume:", {
      initialVolume,
      voiceEnabled,
    });
    setEmotiiVoice(voiceEnabled, "init_from_volume");
    LocalStorage.setEmotiiVoice(voiceEnabled);
  }, [setEmotiiVoice]);

  useEffect(() => {
    return () => {
      clearPendingConferenceTranslationUpdate("unmount");
      translatedMessagesSpeakerRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    const userId = userRef.current?.id ?? user?.id;
    const callUuid = activeCallRef.current?.details?.uuid;

    if (!userId || !callUuid || typeof emotiiVoice !== "boolean") {
      return;
    }

    const lastPayload = lastVoiceStateRef.current;

    if (
      lastPayload.callUuid === callUuid &&
      lastPayload.voice === emotiiVoice
    ) {
      return;
    }

    ccLog.debug("[CallComponent] Sending voice state message", {
      callUuid,
      userId,
      voice_enabled: emotiiVoice,
    });
    sendVoiceEnabledMessage(userId, emotiiVoice);
    lastVoiceStateRef.current = {
      callUuid,
      voice: emotiiVoice,
    };
  }, [emotiiVoice, selectedCall, user?.id, sendVoiceEnabledMessage]);

  useEffect(() => {
    let active = true;

    const handleLanguageChanged = (language) => {
      if (!active) return;

      if (userRef.current && userRef.current.call_language !== language) {
        userRef.current = {
          ...userRef.current,
          call_language: language
        };
      }

      if (
        activeCallRef.current?.details?.conference_id &&
        translationEnabledRef.current &&
        latestTranslationTypeRef.current !== DISABLED_TRANSLATION
      ) {
        handleSendTranslationEnabledMessageRef.current(latestTranslationTypeRef.current);
      }
    };

    const unsubscribeElectron = window.electron?.onLanguageChanged?.(handleLanguageChanged);
    const unsubscribeSync = languageSyncManager.onLanguageChanged(handleLanguageChanged);

    return () => {
      active = false;
      if (typeof unsubscribeElectron === "function") unsubscribeElectron();
      unsubscribeSync();
    };
  }, []);

  useEffect(() => {
    if (translationType === DISABLED_TRANSLATION) {
      translatedMessagesSpeakerRef.current.stop();
      translatedMessagesSpeakerRef.current.markAllAsSpoken(
        currentSpeechRecongnitionMessagesRef.current
      );
      if (useRealtimeApi) {
        stopRealtimeAudioPlayback();
      }
    }
  }, [translationType]);

  return (
    <>
      <MeetingJoinRequestQueue
        selectedCall={selectedCall}
        joinRequestList={joinRequestList}
        setJoinRequestList={setJoinRequestList}
        currentMeetingRef={currentMeetingRef}
        sendApproveJoinMeetingMessage={sendApproveJoinMeetingMessage}
        sendDenyJoinMeetingMessage={sendDenyJoinMeetingMessage}
        atCapacity={
          meetingCapacity.maxParticipants !== null &&
          meetingCapacity.approvedParticipantsCount >=
            meetingCapacity.maxParticipants
        }
      />

      {selectedCall && selectedCallAccess?.type === "mux_stream" && (
        <ConferenceBroadcastView
          data={selectedCall}
          callAccess={selectedCallAccess}
          resetCall={resetCall}
          speakersQueue={speakersQueue}
          handleRequestSpeakMessage={handleRequestSpeakMessage}
          handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
          speechRecongnitionMessages={speechRecongnitionMessages}
          translationType={translationType}
          chatAllowed={broadcastChatAllowed}
          pollsAllowed={broadcastPollsAllowed}
          callChatConversation={callChatConversation}
          onConversationCreated={(conv) => {
            setCallChatConversation(conv);
            callChatConversationIdRef.current = conv.id;
          }}
          unreadChatCount={unreadChatCount}
          activePollBadge={activePollBadge}
          onChatOpened={() => setUnreadChatCount(0)}
          onPollOpened={() => setActivePollBadge(false)}
          activeCallRef={activeCallRef}
          addObserver={add_observer}
          removeObserver={remove_observer}
        />
      )}

      {selectedCall && selectedCallAccess?.type !== "mux_stream" && (
        <LiveKitRoom
          serverUrl={selectedCallAccess?.endpoint}
          token={selectedCallAccess?.token}
          audio={shouldPublishAudio}
          video={shouldPublishVideo}
          options={{ adaptiveStream: true, dynacast: true }}
          style={{
            height: "100%",
            width: "100%",
            zIndex: "20",
            display: "flex",
            flexDirection: "column",
            position: "absolute",
          }}
        >
          <CallView
            raisedHands={raisedHands}
            handleRaiseHand={handleRaiseHand}
            handleLowerHand={handleLowerHand}
            data={selectedCall}
            callAccess={selectedCallAccess}
            resetCall={resetCall}
            setLoading={setLoading}
            activeCallRef={activeCallRef}
            setSelectedCall={setSelectedCall}
            handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
            translationEnabled={translationEnabled}
            setTranslationEnabled={setTranslationEnabled}
            translationEnabledRef={translationEnabledRef}
            translationDisabledManuallyRef={translationDisabledManuallyRef}
            speechRecongnitionMessages={speechRecongnitionMessages}
            handleRequestSpeakMessage={handleRequestSpeakMessage}
            speakersQueue={speakersQueue}
            translationType={translationType}
            setTranslationType={setTranslationType}
            handleSendTranslationEnabledMessage={
              handleSendTranslationEnabledMessage
            }
            conferenceTranslationModeChangePending={Boolean(
              selectedCall?.conference_id && conferenceTranslationModeChangePending
            )}
            setEmotiiVoice={setEmotiiVoice}
            emotiiVoice={emotiiVoice}
            onEmotiiVolumeChange={(volume) => translatedMessagesSpeakerRef.current.setVolume(volume / 100)}
            setCurrentSpeakerIsActive={setCurrentSpeakerIsActive}
            callStartTime={callStartTime}
            setCallStartTime={setCallStartTime}
            useRealtimeApi={useRealtimeApi}
            useLiveKitAgentAudio={useLiveKitAgentAudio}
            realtimeConnected={realtimeConnected}
            realtimeTranslations={realtimeTranslations}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            realtimeAudioPlaying={isRealtimeAudioPlaying}
            aiSummaryEnabled={aiSummaryEnabled}
            setAiSummaryEnabled={setAiSummaryEnabled}
            transcriptExportEnabled={transcriptExportEnabled}
            setTranscriptExportEnabled={setTranscriptExportEnabled}
          />
        </LiveKitRoom>
      )}
    </>
  );
};

export default CallComponent;
